<script setup lang="ts">
import { ref } from 'vue'
import { useEnvironmentStore } from '@/stores/environmentStore'
import { vOnClickOutside } from '@vueuse/components'
import { useToast } from 'vue-toast-notification'
import { useI18n } from 'vue-i18n'
import { PALETTE_OPTIONS, type PaletteId } from '@/ui/palettes'
import { palettePreviewCss } from '@/ui/palettes'

const { t } = useI18n()
const toast = useToast({ position: 'bottom', queue: true, duration: 1000 })
const env = useEnvironmentStore()

const isVisible = ref(false)
const preview = (id: PaletteId) => palettePreviewCss(id)
function toggleSelect() {
  isVisible.value = !isVisible.value
}

function forceClose() {
  isVisible.value = false
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') forceClose()
}

function switchPalette(newPaletteId: PaletteId) {
  toggleSelect()
  env.setPaletteId(newPaletteId)
  toast.info(t('toast.palette-changed', [t(`palette.${newPaletteId}`)]), { position: 'bottom' })
}
</script>

<template>
  <div class="custom-select" v-on-click-outside="forceClose">
    <button
      class="select-button"
      @click="toggleSelect"
      @keydown="onKeydown"
      aria-haspopup="listbox"
      :aria-expanded="isVisible"
    >
    <span class="selected-value">
      <span class="swatches">
        <span class="swatch" :style="{ backgroundColor: preview(env.paletteId).neutral }"></span>
        <span class="swatch" :style="{ backgroundColor: preview(env.paletteId).low }"></span>
        <span class="swatch" :style="{ backgroundColor: preview(env.paletteId).mid }"></span>
        <span class="swatch" :style="{ backgroundColor: preview(env.paletteId).high }"></span>
      </span>
      <span class="label">{{ t(`palette.${env.paletteId}`) }}</span>
    </span>
    <span class="arrow"></span>

    </button>

    <ul
      class="select-dropdown"
      v-if="isVisible"
      role="listbox"
      @keydown="onKeydown"
      v-adjust-position
    >
      <li
        v-for="opt in PALETTE_OPTIONS"
        :key="opt.id"
        role="option"
        :aria-selected="env.paletteId === opt.id"
      >
        <button
          @click="switchPalette(opt.id)"
          class="item"
          :class="{ selected: env.paletteId === opt.id }"
        >
          <span class="swatches">
            <span class="swatch" :style="{ backgroundColor: preview(opt.id).neutral }"></span>
            <span class="swatch" :style="{ backgroundColor: preview(opt.id).low }"></span>
            <span class="swatch" :style="{ backgroundColor: preview(opt.id).mid }"></span>
            <span class="swatch" :style="{ backgroundColor: preview(opt.id).high }"></span>
          </span>
          <span class="label">{{ t(`palette.${opt.id}`) }}</span>
        </button>

      </li>
    </ul>

  </div>
</template>
<style scoped>

ul,
li {
  margin: 0;
  padding: 0;
  list-style: none;
}

.custom-select {
  position: relative;
  display: inline-block;
  width: 100%;
  max-width: 320px; /* settings-friendly width */
}

.select-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 38px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;

  background: var(--color-app-background);
  border: 1px solid var(--color-menu-divider-2);
  color: var(--color-text);
}

.select-button:focus-visible {
  outline: 2px solid var(--color-menu);
  outline-offset: 2px;
}

.selected-value {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  min-width: 0; /* allow ellipsis */
}

.selected-value .label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 260px;

  background: var(--color-app-background); /* NOT transparent */
  border: 1px solid var(--color-menu-divider-2);
  border-radius: 10px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.35);

  max-height: 240px;
  overflow-y: auto;
  z-index: 10;
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between; /* swatches+label group left, optional check right later */
  gap: 12px;

  width: 100%;
  padding: 10px 12px;

  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--color-text);
  text-align: left;
}

.item + .item {
  border-top: 1px solid var(--color-menu-divider-2);
}

.item:hover,
.item:focus {
  background-color: var(--color-menu);
}

.item.selected {
  font-weight: 700;
}

.item .label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.arrow {
  margin-left: 12px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid var(--color-text);
  transition: transform ease-in-out 0.2s;
}

.swatches {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex: 0 0 auto;
}

.swatch {
  width: 12px;
  height: 12px;
  border-radius: 999px; /* circles read better */
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.15) inset;
}
</style>
